// Painting to Gogh products have asymmetric option matrices: the
// option-2 values for "Date Night" (e.g. "Date Night Studio Kit")
// don't pair with "Solo" (which has its own "Studio Kit" value).
// Stock variant-picker fetches the new option-1 with the previous
// option-2 still selected — Shopify's Liquid keeps that value as
// `selected` even though no matching variant exists, leaving the
// fieldset stuck on a hidden (and unavailable) row.
//
// Fix: in the change-event capture phase (before variant-picker's
// bubble-phase listener reads selectedOptionsValues), find the
// closest-available variant for the user's just-clicked value and
// preemptively update other fieldsets to match. Single fetch, no
// flicker. Port of the legacy Archetype Impulse "Dynamic Variants"
// algorithm — see ../current-theme/assets/block-variant-picker.js.
//
// Variant data is inlined per-PDP via product-information.liquid as
// <script type="application/json" data-cd-variants="<product-id>">.

/**
 * @param {HTMLInputElement} target
 * @param {HTMLElement} picker
 * @returns {boolean} true if any radio was changed
 */
const preemptivelyUpdate = (target, picker) => {
  const productId = picker.dataset.productId;
  const variantsScript = document.querySelector(`script[data-cd-variants="${productId}"]`);
  if (!variantsScript) return false;

  let variants;
  try {
    variants = JSON.parse(variantsScript.textContent);
  } catch {
    return false;
  }
  if (!Array.isArray(variants) || variants.length === 0) return false;

  const fieldsets = Array.from(picker.querySelectorAll('fieldset.variant-option'));
  if (fieldsets.length < 2) return false;

  const clickedFieldset = target.closest('fieldset.variant-option');
  const clickedIdx = fieldsets.indexOf(clickedFieldset);
  if (clickedIdx === -1) return false;

  const currentValues = fieldsets.map((fs) => {
    const checked = fs.querySelector('input[type="radio"]:checked');
    return checked?.value;
  });

  const fullMatch = variants.find(
    (v) => v.available && v.options.every((opt, i) => opt === currentValues[i])
  );
  if (fullMatch) return false;

  const candidates = variants.filter((v) => {
    if (!v.available) return false;
    for (let i = 0; i <= clickedIdx; i++) {
      if (v.options[i] !== currentValues[i]) return false;
    }
    return true;
  });
  if (candidates.length === 0) return false;

  const weight = (i) => fieldsets.length - i;
  const score = (v) =>
    v.options.reduce((sum, opt, i) => (opt === currentValues[i] ? sum + weight(i) : sum), 0);
  const best = candidates.reduce(
    (acc, v) => (acc === null || score(v) > score(acc) ? v : acc),
    null
  );
  if (!best) return false;

  let changed = false;
  fieldsets.forEach((fs, i) => {
    if (i === clickedIdx) return;
    const wantedValue = best.options[i];
    const radios = Array.from(fs.querySelectorAll('input[type="radio"]'));
    const wanted = radios.find((r) => r.value === wantedValue);
    if (!wanted || wanted.checked) return;
    radios.forEach((r) => {
      const shouldBeChecked = r === wanted;
      if (r.checked !== shouldBeChecked) r.checked = shouldBeChecked;
      r.dataset.currentChecked = shouldBeChecked ? 'true' : 'false';
      if (r.dataset.previousChecked === 'true' && !shouldBeChecked) {
        r.dataset.previousChecked = 'false';
      }
    });
    changed = true;
  });
  return changed;
};

document.addEventListener(
  'change',
  (event) => {
    if (!event.isTrusted) return;
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    const fieldset = target.closest('fieldset.variant-option');
    if (!fieldset) return;
    const picker = fieldset.closest('variant-picker');
    if (!picker || picker.dataset.templateProductMatch !== 'true') return;
    preemptivelyUpdate(target, picker);
  },
  true
);

// Defensive post-morph sync: morph's onBeforeUpdate (assets/morph.js)
// preserves any truthy old data-current-checked value across morphs,
// which can leave the visual pill stale if the preemptive update
// above didn't fire (e.g. initial-load deep links to a variant the
// rest of the matrix can't match).
// 'variant:update' literal — see ThemeEvents.variantUpdate in events.js.
// Hardcoded so this module doesn't need the importmap (it loads before
// the importmap render in layout/theme.liquid; bare-specifier imports
// would fail silently otherwise).
document.addEventListener('variant:update', (event) => {
  const productId = event?.detail?.data?.productId;
  if (!productId) return;
  const picker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
  if (!picker) return;

  picker.querySelectorAll('fieldset.variant-option input[type="radio"]').forEach((radio) => {
    const desired = radio.checked ? 'true' : 'false';
    if (radio.dataset.currentChecked !== desired) {
      radio.dataset.currentChecked = desired;
    }
    if (radio.dataset.previousChecked === 'true' && !radio.checked) {
      radio.dataset.previousChecked = 'false';
    }
  });
});
